import { useState, useEffect, useCallback, useRef } from "react";
import { account } from "@/lib/appwrite";
import { useSubjects } from "@/contexts/SubjectsContext";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
import "katex/dist/katex.min.css";
import {
  Search, ChevronDown, Edit2, Trash2, X, Save, Loader2,
  AlertCircle, CheckCircle2, LogOut, ChevronLeft, ChevronRight,
  RefreshCw, Eye, EyeOff, FileText, Tag, ArrowUp, Sparkles, Square,
} from "lucide-react";

async function adminFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const { jwt } = await account.createJWT();
  return fetch(url, {
    ...options,
    headers: { ...(options.headers ?? {}), Authorization: `Bearer ${jwt}` },
  });
}

// ── Types ────────────────────────────────────────────────────────────────────

interface DBQuestion {
  $id: string;
  subjectId: string;
  moduleId: string;
  topicId: string;
  subtopicId?: string;
  difficulty: "easy" | "medium" | "hard";
  question: string;
  passage?: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  correctAnswer: "A" | "B" | "C" | "D";
  explanation: string;
  category?: string;
  skill?: string;
  isActive?: boolean;
  createdAt?: string;
}

const DIFFICULTIES = ["easy", "medium", "hard"] as const;
const PAGE_SIZE = 25;

function diffBadge(d: string) {
  return d === "easy"
    ? "bg-green-100 text-green-700"
    : d === "medium"
    ? "bg-yellow-100 text-yellow-700"
    : "bg-red-100 text-red-700";
}

// ── Filter select ────────────────────────────────────────────────────────────

function FilterSelect({
  label,
  value,
  onChange,
  options,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-[#5a6a7e] mb-1 block">{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          className="w-full appearance-none border border-[#dde3ed] rounded-xl px-3 py-2 pr-7 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/20 focus:border-[#1a3a6b] bg-white disabled:opacity-40 disabled:cursor-not-allowed truncate"
        >
          <option value="">All</option>
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-2 top-2.5 w-3.5 h-3.5 text-[#5a6a7e] pointer-events-none" />
      </div>
    </div>
  );
}

// ── Edit modal ───────────────────────────────────────────────────────────────

function EditModal({
  question,
  onSave,
  onClose,
}: {
  question: DBQuestion;
  onSave: (updated: DBQuestion) => Promise<void>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<DBQuestion>({ ...question });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
    } catch (e: any) {
      setError(e.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const field = (label: string, key: keyof DBQuestion, multiline = false, highlight = false) => (
    <div>
      <label className={`text-xs font-medium mb-1 block ${highlight ? "text-green-700" : "text-gray-500"}`}>
        {label}
      </label>
      {multiline ? (
        <textarea
          rows={key === "question" || key === "passage" ? 4 : 3}
          value={(draft[key] as string) ?? ""}
          onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
          className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/30 resize-y ${highlight ? "border-green-300 bg-green-50" : "border-gray-200"}`}
        />
      ) : (
        <input
          type="text"
          value={(draft[key] as string) ?? ""}
          onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
          className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/30 ${highlight ? "border-green-300 bg-green-50" : "border-gray-200"}`}
        />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="font-semibold text-[#1a2435]">Edit Question</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-4 flex-1">
          {field("Passage (optional)", "passage", true)}
          {field("Question", "question", true)}

          <div className="grid grid-cols-2 gap-3">
            {field("Option A", "optionA", false, draft.correctAnswer === "A")}
            {field("Option B", "optionB", false, draft.correctAnswer === "B")}
            {field("Option C", "optionC", false, draft.correctAnswer === "C")}
            {field("Option D", "optionD", false, draft.correctAnswer === "D")}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Correct Answer</label>
              <div className="relative">
                <select
                  value={draft.correctAnswer}
                  onChange={e => setDraft(d => ({ ...d, correctAnswer: e.target.value as any }))}
                  className="w-full appearance-none border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/30 bg-white"
                >
                  {["A", "B", "C", "D"].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Difficulty</label>
              <div className="relative">
                <select
                  value={draft.difficulty}
                  onChange={e => setDraft(d => ({ ...d, difficulty: e.target.value as any }))}
                  className="w-full appearance-none border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/30 bg-white"
                >
                  {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {field("Explanation", "explanation", true)}

          <div className="grid grid-cols-3 gap-3">
            {field("Subject ID", "subjectId")}
            {field("Module ID", "moduleId")}
            {field("Topic ID", "topicId")}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {field("Skill", "skill")}
            {field("Category", "category")}
          </div>

          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-gray-500">Visibility</label>
            <button
              onClick={() => setDraft(d => ({ ...d, isActive: !d.isActive }))}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${
                draft.isActive
                  ? "bg-green-50 border-green-300 text-green-700"
                  : "bg-gray-50 border-gray-300 text-gray-500"
              }`}
            >
              {draft.isActive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {draft.isActive ? "Active" : "Hidden"}
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 shrink-0" /> {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#1a3a6b] text-white text-sm font-medium rounded-lg hover:bg-[#0f2548] disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Combobox field (free-text + datalist suggestions + optional null) ─────────

const NULL_SENTINEL = "__null__";

function ComboField({
  label,
  value,
  onChange,
  options,
  nullable = false,
  listId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  nullable?: boolean;
  listId: string;
}) {
  const isNull = value === NULL_SENTINEL;
  return (
    <div>
      <label className="text-xs font-medium text-gray-500 mb-1 block">{label}</label>
      <div className="flex gap-1.5">
        {isNull ? (
          <div className="flex-1 flex items-center gap-2 border border-red-200 rounded-lg px-3 py-2 text-sm bg-red-50">
            <span className="text-red-400 italic text-xs">set to null</span>
            <button type="button" onClick={() => onChange("")} className="ml-auto text-red-300 hover:text-red-500">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="relative flex-1">
            <input
              type="text"
              value={value}
              onChange={e => onChange(e.target.value)}
              list={listId}
              placeholder="— no change —"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/30 bg-white"
            />
            <datalist id={listId}>
              {options.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </datalist>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                className="absolute right-2 top-2.5 text-gray-300 hover:text-gray-500"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
        {nullable && !isNull && (
          <button
            type="button"
            onClick={() => onChange(NULL_SENTINEL)}
            title="Explicitly set this field to null"
            className="px-2.5 text-xs border border-gray-200 rounded-lg text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors font-mono"
          >
            null
          </button>
        )}
      </div>
    </div>
  );
}

// ── Bulk recategorize modal ───────────────────────────────────────────────────

function BulkRecategorizeModal({
  count,
  allMatching,
  onApply,
  onClose,
}: {
  count: number;
  allMatching: boolean;
  onApply: (patch: Record<string, string | null>) => Promise<void>;
  onClose: () => void;
}) {
  const { subjects } = useSubjects();
  const [subjectId, setSubjectId]   = useState("");
  const [moduleId, setModuleId]     = useState("");
  const [topicId, setTopicId]       = useState("");
  const [subtopicId, setSubtopicId] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const selectedSubject = subjects.find(s => s.id === subjectId);
  const selectedModule  = selectedSubject?.modules.find(m => m.id === moduleId);
  const selectedTopic   = selectedModule?.topics.find(t => t.id === topicId);

  async function handleApply() {
    const patch: Record<string, string | null> = {};
    const add = (key: string, val: string) => {
      if (val === NULL_SENTINEL) patch[key] = null;
      else if (val) patch[key] = val;
    };
    add("subjectId",  subjectId);
    add("moduleId",   moduleId);
    add("topicId",    topicId);
    add("subtopicId", subtopicId);
    add("difficulty", difficulty);
    if (!Object.keys(patch).length) { setError("Set at least one field to change."); return; }
    setSaving(true);
    setError(null);
    try { await onApply(patch); }
    catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  }

  const diffSelect = (
    <div>
      <label className="text-xs font-medium text-gray-500 mb-1 block">Difficulty</label>
      <div className="relative">
        <select
          value={difficulty}
          onChange={e => setDifficulty(e.target.value)}
          className="w-full appearance-none border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/30"
        >
          <option value="">— no change —</option>
          {DIFFICULTIES.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
        </select>
        <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-[#1a2435]">
            Recategorize {allMatching ? `all ${count.toLocaleString()}` : count} question{count !== 1 ? "s" : ""}
            {allMatching && <span className="ml-1.5 text-xs font-normal text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">all matching</span>}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-3">
          <p className="text-xs text-gray-400">Type a new value or pick from suggestions. Leave blank to keep existing. Click <span className="font-mono">null</span> to explicitly clear a field.</p>

          <ComboField
            label="Subject"
            value={subjectId}
            onChange={v => { setSubjectId(v); setModuleId(""); setTopicId(""); setSubtopicId(""); }}
            options={subjects.map(s => ({ value: s.id, label: s.title }))}
            listId="combo-subject"
          />

          <ComboField
            label="Module"
            value={moduleId}
            onChange={v => { setModuleId(v); setTopicId(""); setSubtopicId(""); }}
            options={(selectedSubject?.modules ?? subjects.flatMap(s => s.modules)).map(m => ({ value: m.id, label: m.title }))}
            listId="combo-module"
          />

          <ComboField
            label="Topic"
            value={topicId}
            onChange={v => { setTopicId(v); setSubtopicId(""); }}
            options={(selectedModule?.topics ?? (selectedSubject?.modules ?? subjects.flatMap(s => s.modules)).flatMap(m => m.topics)).map(t => ({ value: t.id, label: t.name }))}
            listId="combo-topic"
          />

          <div className="relative">
            <ComboField
              label="Subtopic"
              value={subtopicId}
              onChange={setSubtopicId}
              options={(selectedTopic?.subtopics ?? []).map(s => ({ value: s.id, label: s.name }))}
              nullable
              listId="combo-subtopic"
            />
            {subtopicId && subtopicId !== NULL_SENTINEL && (
              <button
                type="button"
                onClick={() => { setTopicId(subtopicId); setSubtopicId(NULL_SENTINEL); }}
                title="Promote: move this subtopic ID into the Topic field"
                className="absolute -top-0.5 right-0 flex items-center gap-1 text-[10px] text-[#1a3a6b] hover:text-[#0f2548] font-medium"
              >
                <ArrowUp className="w-3 h-3" /> promote to topic
              </button>
            )}
          </div>

          {diffSelect}

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 shrink-0" /> {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleApply}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#1a3a6b] text-white text-sm font-medium rounded-lg hover:bg-[#0f2548] disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
            Apply to {allMatching ? `all ${count.toLocaleString()}` : count}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Refine explanations modal ─────────────────────────────────────────────────

type RefineStatus = "idle" | "running" | "stopped" | "done";

function RefineModal({
  questions,
  onClose,
  onExplanationUpdated,
}: {
  questions: DBQuestion[];
  onClose: () => void;
  onExplanationUpdated: (id: string, explanation: string) => void;
}) {
  const [status, setStatus] = useState<RefineStatus>("idle");
  const [processed, setProcessed] = useState(0);
  const [updated, setUpdated] = useState(0);
  const [errors, setErrors] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState<string | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const total = questions.length;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setStatus("running");
      try {
        const { jwt } = await account.createJWT();
        const res = await fetch("/api/admin/refine-explanations", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
          body: JSON.stringify({
            questions: questions.map(q => ({
              $id: q.$id,
              question: q.question,
              optionA: q.optionA,
              optionB: q.optionB,
              optionC: q.optionC,
              optionD: q.optionD,
              correctAnswer: q.correctAnswer,
              explanation: q.explanation,
            })),
          }),
        });

        if (!res.ok || !res.body) {
          setStatus("done");
          return;
        }

        const reader = res.body.getReader();
        readerRef.current = reader;
        const decoder = new TextDecoder();
        let buffer = "";

        while (!cancelled) {
          const { done: streamDone, value } = await reader.read();
          if (streamDone) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === "progress") {
                setProcessed(event.processed);
                setUpdated(event.updated);
                setErrors(event.errors);
                const q = questions[event.processed - 1];
                if (q) setCurrentQuestion(q.question.slice(0, 80));
                if (event.updatedId && event.updatedExplanation) {
                  onExplanationUpdated(event.updatedId, event.updatedExplanation);
                }
              } else if (event.type === "done") {
                setProcessed(event.processed);
                setUpdated(event.updated);
                setErrors(event.errors);
                setCurrentQuestion(null);
                setStatus("done");
              }
            } catch {}
          }
        }

        if (cancelled) setStatus("stopped");
      } catch {
        setStatus("done");
      }
    })();

    return () => {
      cancelled = true;
      readerRef.current?.cancel();
    };
  }, []);

  function handleStop() {
    readerRef.current?.cancel();
    readerRef.current = null;
    setStatus("stopped");
    setCurrentQuestion(null);
  }

  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isFinished = status === "done" || status === "stopped";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <h2 className="font-semibold text-[#1a2435]">
              Rewriting {total} explanation{total !== 1 ? "s" : ""}
            </h2>
          </div>
          {isFinished && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="h-2.5 bg-[#f0f2f5] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                status === "stopped" ? "bg-amber-400" : "bg-[#1a3a6b]"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-[#5a6a7e]">
            <span>
              {status === "running" && `Processing…`}
              {status === "stopped" && "Stopped"}
              {status === "done" && "Complete"}
              {status === "idle" && "Starting…"}
            </span>
            <span>{processed} / {total}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-green-50 border border-green-100 rounded-xl py-3">
            <p className="text-2xl font-bold text-green-600">{updated}</p>
            <p className="text-xs text-[#5a6a7e] mt-0.5">Rewritten</p>
          </div>
          <div className="bg-[#f8f9fc] border border-[#eaecf0] rounded-xl py-3">
            <p className="text-2xl font-bold text-[#1a2435]">{total - processed}</p>
            <p className="text-xs text-[#5a6a7e] mt-0.5">Remaining</p>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl py-3">
            <p className="text-2xl font-bold text-red-500">{errors}</p>
            <p className="text-xs text-[#5a6a7e] mt-0.5">Errors</p>
          </div>
        </div>

        {/* Current question hint */}
        {currentQuestion && (
          <p className="text-xs text-[#5a6a7e] truncate bg-[#f8f9fc] rounded-lg px-3 py-2 border border-[#eaecf0]">
            <span className="font-medium text-[#1a2435]">Current: </span>{currentQuestion}…
          </p>
        )}

        {/* Footer action */}
        <div className="flex justify-end pt-1">
          {!isFinished ? (
            <button
              onClick={handleStop}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700 transition-colors"
            >
              <Square className="w-3.5 h-3.5 fill-white" /> Stop
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-5 py-2 bg-[#1a3a6b] text-white text-sm font-medium rounded-xl hover:bg-[#0f2548] transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Question card — practice-test style ──────────────────────────────────────

function QuestionCard({
  q,
  index,
  selected,
  onSelect,
  onEdit,
  onDelete,
  onToggleActive,
}: {
  q: DBQuestion;
  index: number;
  selected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const options = (["A", "B", "C", "D"] as const).map(id => ({
    id,
    text: q[`option${id}` as keyof DBQuestion] as string,
    correct: q.correctAnswer === id,
  }));

  return (
    <div
      className={`rounded-2xl border overflow-hidden transition-all ${q.isActive === false ? "opacity-50" : ""} ${selected ? "ring-2 ring-[#1a3a6b]" : ""}`}
      style={{ borderColor: selected ? "#1a3a6b" : "#dde3ed" }}
    >

      {/* Card header */}
      <div className="flex items-center justify-between px-5 py-3 bg-[#f8f9fc] border-b border-[#dde3ed]">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="checkbox"
            checked={selected}
            onChange={onSelect}
            className="accent-[#1a3a6b] w-4 h-4 shrink-0 cursor-pointer"
          />
          <span className="text-xs font-semibold text-[#5a6a7e]">#{index}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${diffBadge(q.difficulty)}`}>
            {q.difficulty}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">
            {q.subjectId}
          </span>
          {q.topicId && (
            <span className="text-xs text-[#5a6a7e]">{q.topicId}</span>
          )}
          {q.skill && (
            <span className="text-xs text-[#5a6a7e] italic truncate max-w-[220px]">{q.skill}</span>
          )}
          {q.isActive === false && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">hidden</span>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0 ml-3">
          <button
            onClick={onToggleActive}
            title={q.isActive === false ? "Make active" : "Hide"}
            className="p-1.5 rounded-lg hover:bg-white text-[#5a6a7e] hover:text-[#1a2435] transition-colors"
          >
            {q.isActive === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 rounded-lg hover:bg-white text-[#5a6a7e] hover:text-[#1a3a6b] transition-colors"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          {confirmDelete ? (
            <div className="flex items-center gap-1">
              <button
                onClick={onDelete}
                className="px-2.5 py-1 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                Delete
              </button>
              <button onClick={() => setConfirmDelete(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="p-1.5 rounded-lg hover:bg-white text-[#5a6a7e] hover:text-red-500 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Question body */}
      <div className="bg-white px-6 py-5 space-y-4">

        {/* Passage */}
        {q.passage && (
          <div className="bg-[#f0f4ff] border border-[#d0d9f0] rounded-xl px-4 py-3">
            <MarkdownRenderer content={q.passage} className="text-sm text-[#1a2435] leading-relaxed" />
          </div>
        )}

        {/* Question text */}
        <MarkdownRenderer content={q.question} className="text-[#1a2435] font-medium text-[15px] leading-relaxed" />

        {/* Options */}
        <div className="space-y-2">
          {options.map(({ id, text, correct }) => (
            <div
              key={id}
              className={`flex items-start gap-3 px-4 py-3 rounded-xl border-2 transition-colors ${
                correct
                  ? "border-green-400 bg-green-50"
                  : "border-[#e8ecf3] bg-[#fafbfc]"
              }`}
            >
              <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold mt-0.5 ${
                correct ? "bg-green-500 text-white" : "bg-[#e8ecf3] text-[#5a6a7e]"
              }`}>
                {id}
              </span>
              <MarkdownRenderer
                content={text}
                className={`text-sm leading-relaxed flex-1 ${correct ? "text-green-800 font-medium" : "text-[#1a2435]"}`}
              />
              {correct && <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />}
            </div>
          ))}
        </div>

        {/* Explanation */}
        {q.explanation && (
          <div className="border-t border-[#dde3ed] pt-4">
            <p className="text-xs font-semibold text-[#5a6a7e] uppercase tracking-wide mb-1.5">Explanation</p>
            <MarkdownRenderer content={q.explanation} className="text-sm text-[#1a2435] leading-relaxed" />
          </div>
        )}

        <p className="text-[10px] text-gray-300 font-mono">ID: {q.$id}</p>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function AdminQuestionsPage() {
  const { subjects } = useSubjects();
  const [questions, setQuestions] = useState<DBQuestion[]>([]);
  const [total, setTotal]         = useState<number | null>(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const [search, setSearch]           = useState("");
  const [subjectId, setSubjectId]     = useState("");
  const [moduleId, setModuleId]       = useState("");
  const [topicId, setTopicId]         = useState("");
  const [subtopicId, setSubtopicId]   = useState("");
  const [difficulty, setDifficulty]   = useState("");

  const selectedSubject = subjects.find(s => s.id === subjectId);
  const selectedModule  = selectedSubject?.modules.find(m => m.id === moduleId);
  const selectedTopic   = selectedModule?.topics.find(t => t.id === topicId);

  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [pageIdx, setPageIdx] = useState(0);

  const [editingQ, setEditingQ] = useState<DBQuestion | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [refineQuestions, setRefineQuestions] = useState<DBQuestion[] | null>(null);

  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [fixingModule, setFixingModule] = useState(false);
  const [promotingSubtopic, setPromotingSubtopic] = useState(false);

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === questions.length) {
      setSelectedIds(new Set());
      setSelectAllMatching(false);
    } else {
      setSelectedIds(new Set(questions.map(q => q.$id)));
      setSelectAllMatching(false);
    }
  }

  async function handleBulkRecategorize(patch: Record<string, string | null>) {
    if (selectAllMatching) {
      // Server-side: apply patch to all docs matching current filters
      const filters: Record<string, string> = {};
      if (subjectId)  filters.subjectId  = subjectId;
      if (moduleId)   filters.moduleId   = moduleId;
      if (topicId)    filters.topicId    = topicId;
      if (subtopicId) filters.subtopicId = subtopicId;
      if (difficulty) filters.difficulty = difficulty;
      const res = await adminFetch("/api/admin/bulk-recategorize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filters, patch }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSelectedIds(new Set());
      setSelectAllMatching(false);
      setShowBulkModal(false);
      showToast(`Updated ${data.updated} questions${data.errors ? ` (${data.errors} errors)` : ""}.`, !data.errors);
      fetchPage(cursors[pageIdx] ?? null, { search, subjectId, moduleId, topicId, subtopicId, difficulty });
    } else {
      // Client-side: update only the selected IDs
      const ids = [...selectedIds];
      let done = 0;
      for (const id of ids) {
        const res = await adminFetch(`/api/admin/questions/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        });
        const data = await res.json();
        if (!data.error) {
          done++;
          setQuestions(prev => prev.map(q => q.$id === id ? { ...q, ...patch } : q));
        }
      }
      setSelectedIds(new Set());
      setShowBulkModal(false);
      showToast(`Updated ${done} of ${ids.length} questions.`, done === ids.length);
    }
  }

  function showToast(msg: string, ok: boolean) {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, ok });
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }

  async function fixReadingModule() {
    if (!confirm("This will migrate SAT reading/writing questions: fix moduleId to 'reading_and_writing', rename info_ideas → information_and_ideas, and standardize subtopicIds. Continue?")) return;
    setFixingModule(true);
    try {
      const res = await adminFetch("/api/admin/fix-reading-module", { method: "POST" });
      const data = await res.json();
      if (data.error) { showToast(data.error, false); return; }
      showToast(`Fixed ${data.fixed} questions (${data.skipped} already correct, ${data.errors} errors)`, data.errors === 0);
      fetchPage(cursors[pageIdx] ?? null, { search, subjectId, moduleId, topicId, subtopicId, difficulty });
    } catch (e: any) {
      showToast(e.message, false);
    } finally {
      setFixingModule(false);
    }
  }

  async function promoteSubtopicToTopic() {
    const scope = [subjectId, moduleId, topicId, subtopicId, difficulty].filter(Boolean);
    const scopeDesc = scope.length ? `filtered scope (${scope.join(", ")})` : "ALL questions (no filters active)";
    if (!confirm(`This will copy subtopicId → topicId and clear subtopicId for every question in: ${scopeDesc}.\n\nContinue?`)) return;
    setPromotingSubtopic(true);
    try {
      const filters: Record<string, string> = {};
      if (subjectId)  filters.subjectId  = subjectId;
      if (moduleId)   filters.moduleId   = moduleId;
      if (topicId)    filters.topicId    = topicId;
      if (subtopicId) filters.subtopicId = subtopicId;
      if (difficulty) filters.difficulty = difficulty;
      const res = await adminFetch("/api/admin/promote-subtopic-to-topic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filters }),
      });
      const data = await res.json();
      if (data.error) { showToast(data.error, false); return; }
      showToast(`Promoted ${data.promoted} questions (${data.skipped} skipped, ${data.errors} errors)`, data.errors === 0);
      fetchPage(cursors[pageIdx] ?? null, { search, subjectId, moduleId, topicId, subtopicId, difficulty });
    } catch (e: any) {
      showToast(e.message, false);
    } finally {
      setPromotingSubtopic(false);
    }
  }

  const fetchPage = useCallback(async (
    cursorVal: string | null,
    filters: { search: string; subjectId: string; moduleId: string; topicId: string; subtopicId: string; difficulty: string },
  ) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (cursorVal)            params.set("cursor",     cursorVal);
      if (filters.subjectId)    params.set("subjectId",  filters.subjectId);
      if (filters.moduleId)     params.set("moduleId",   filters.moduleId);
      if (filters.topicId)      params.set("topicId",    filters.topicId);
      if (filters.subtopicId)   params.set("subtopicId", filters.subtopicId);
      if (filters.difficulty)   params.set("difficulty", filters.difficulty);
      if (filters.search)       params.set("search",     filters.search);

      const res  = await adminFetch(`/api/admin/questions?${params}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setQuestions(data.documents ?? []);
      setTotal(data.total ?? null);
    } catch (e: any) {
      setError(e.message ?? "Failed to load questions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(cursors[pageIdx] ?? null, { search, subjectId, moduleId, topicId, subtopicId, difficulty });
  }, [pageIdx, cursors, search, subjectId, moduleId, topicId, subtopicId, difficulty, fetchPage]);

  function resetPage() {
    setCursors([null]);
    setPageIdx(0);
    setSelectedIds(new Set());
    setSelectAllMatching(false);
  }

  function goNext() {
    const last = questions[questions.length - 1];
    if (!last) return;
    setCursors(prev => { const n = [...prev]; n[pageIdx + 1] = last.$id; return n; });
    setPageIdx(i => i + 1);
    setSelectedIds(new Set());
  }

  function goPrev() {
    if (pageIdx > 0) { setPageIdx(i => i - 1); setSelectedIds(new Set()); }
  }

  async function handleSave(updated: DBQuestion) {
    const { $id, ...fields } = updated;
    const res  = await adminFetch(`/api/admin/questions/${$id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fields),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    setQuestions(prev => prev.map(q => q.$id === $id ? { ...q, ...fields } : q));
    setEditingQ(null);
    showToast("Saved.", true);
  }

  async function handleDelete(id: string) {
    const res  = await adminFetch(`/api/admin/questions/${id}`, { method: "DELETE" });
    const data = await res.json();
    if (data.error) { showToast(data.error, false); return; }
    setQuestions(prev => prev.filter(q => q.$id !== id));
    showToast("Deleted.", true);
  }

  async function handleToggleActive(q: DBQuestion) {
    const newVal = q.isActive !== false;
    const res  = await adminFetch(`/api/admin/questions/${q.$id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ isActive: !newVal }),
    });
    const data = await res.json();
    if (data.error) { showToast(data.error, false); return; }
    setQuestions(prev => prev.map(x => x.$id === q.$id ? { ...x, isActive: !newVal } : x));
  }

  const hasNext  = questions.length === PAGE_SIZE;
  const hasPrev  = pageIdx > 0;
  const startNum = pageIdx * PAGE_SIZE + 1;

  return (
    <div className="min-h-screen bg-[#f8f9fc]">

      {/* Top bar */}
      <div className="bg-white border-b border-[#dde3ed] px-6 py-4 flex items-center gap-3 sticky top-0 z-10 shadow-sm">
        <button
          onClick={() => window.location.href = "/admin"}
          className="text-[#5a6a7e] hover:text-[#1a2435] text-sm font-medium transition-colors"
        >
          ← Admin
        </button>
        <div className="h-4 w-px bg-[#dde3ed]" />
        <FileText className="w-4 h-4 text-[#1a3a6b]" />
        <h1 className="text-base font-semibold text-[#1a2435]">Review Questions</h1>
        {total !== null && (
          <span className="text-sm text-[#5a6a7e]">{total.toLocaleString()} total</span>
        )}
        <div className="flex-1" />
        <button
          onClick={promoteSubtopicToTopic}
          disabled={promotingSubtopic}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-violet-300 text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors disabled:opacity-50"
          title="For each question in the current filter scope: copy subtopicId → topicId and clear subtopicId"
        >
          {promotingSubtopic ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowUp className="w-3.5 h-3.5" />}
          Promote Subtopic → Topic
        </button>
        <button
          onClick={fixReadingModule}
          disabled={fixingModule}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-50"
          title="Migrate SAT reading/writing: fix moduleId and topicIds"
        >
          {fixingModule ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Migrate R&amp;W
        </button>
        <button
          onClick={() => fetchPage(cursors[pageIdx] ?? null, { search, subjectId, moduleId, topicId, subtopicId, difficulty })}
          className="p-2 text-[#5a6a7e] hover:text-[#1a2435] rounded-lg hover:bg-[#f0f2f5] transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        <button
          onClick={async () => { await account.deleteSession("current").catch(() => {}); window.location.href = "/"; }}
          className="flex items-center gap-1.5 text-sm text-[#5a6a7e] hover:text-red-600 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-6 space-y-5">

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-[#dde3ed] p-4 space-y-3 shadow-sm">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-[#5a6a7e]" />
            <input
              type="text"
              placeholder="Search question text…"
              value={search}
              onChange={e => { setSearch(e.target.value); resetPage(); }}
              className="w-full border border-[#dde3ed] rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/20 focus:border-[#1a3a6b]"
            />
          </div>

          {/* Tag dropdowns */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {/* Subject */}
            <FilterSelect
              label="Subject"
              value={subjectId}
              onChange={v => { setSubjectId(v); setModuleId(""); setTopicId(""); setSubtopicId(""); resetPage(); }}
              options={subjects.map(s => ({ value: s.id, label: s.title }))}
            />

            {/* Module */}
            <FilterSelect
              label="Module"
              value={moduleId}
              onChange={v => { setModuleId(v); setTopicId(""); setSubtopicId(""); resetPage(); }}
              disabled={!selectedSubject}
              options={(selectedSubject?.modules ?? []).map(m => ({ value: m.id, label: m.title }))}
            />

            {/* Topic */}
            <FilterSelect
              label="Topic"
              value={topicId}
              onChange={v => { setTopicId(v); setSubtopicId(""); resetPage(); }}
              disabled={!selectedModule}
              options={(selectedModule?.topics ?? []).map(t => ({ value: t.id, label: t.name }))}
            />

            {/* Subtopic */}
            <FilterSelect
              label="Subtopic"
              value={subtopicId}
              onChange={v => { setSubtopicId(v); resetPage(); }}
              disabled={!selectedTopic}
              options={(selectedTopic?.subtopics ?? []).map(s => ({ value: s.id, label: s.name }))}
            />

            {/* Difficulty */}
            <FilterSelect
              label="Difficulty"
              value={difficulty}
              onChange={v => { setDifficulty(v); resetPage(); }}
              options={DIFFICULTIES.map(d => ({ value: d, label: d.charAt(0).toUpperCase() + d.slice(1) }))}
            />
          </div>

          {/* Active filter chips */}
          {(subjectId || moduleId || topicId || subtopicId || difficulty || search) && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {[
                subjectId  && { label: `Subject: ${selectedSubject?.title ?? subjectId}`,   clear: () => { setSubjectId(""); setModuleId(""); setTopicId(""); setSubtopicId(""); resetPage(); } },
                moduleId   && { label: `Module: ${selectedModule?.title ?? moduleId}`,       clear: () => { setModuleId(""); setTopicId(""); setSubtopicId(""); resetPage(); } },
                topicId    && { label: `Topic: ${selectedTopic?.name ?? topicId}`,            clear: () => { setTopicId(""); setSubtopicId(""); resetPage(); } },
                subtopicId && { label: `Subtopic: ${selectedTopic?.subtopics?.find(s => s.id === subtopicId)?.name ?? subtopicId}`, clear: () => { setSubtopicId(""); resetPage(); } },
                difficulty && { label: difficulty,                                             clear: () => { setDifficulty(""); resetPage(); } },
                search     && { label: `"${search}"`,                                          clear: () => { setSearch(""); resetPage(); } },
              ].filter(Boolean).map((chip: any) => (
                <button
                  key={chip.label}
                  onClick={chip.clear}
                  className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#1a3a6b]/10 text-[#1a3a6b] font-medium hover:bg-[#1a3a6b]/20 transition-colors"
                >
                  {chip.label} <X className="w-3 h-3" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {/* Selection toolbar */}
        {questions.length > 0 && !loading && (
          <div className="space-y-2">
            <div className="flex items-center gap-3 bg-white border border-[#dde3ed] rounded-xl px-4 py-2.5 shadow-sm">
              <input
                type="checkbox"
                checked={selectedIds.size === questions.length && questions.length > 0}
                ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < questions.length; }}
                onChange={toggleSelectAll}
                className="accent-[#1a3a6b] w-4 h-4 cursor-pointer"
              />
              <span className="text-sm text-[#5a6a7e]">
                {selectAllMatching
                  ? `All ${total?.toLocaleString()} matching selected`
                  : selectedIds.size > 0
                  ? `${selectedIds.size} selected`
                  : "Select all on page"}
              </span>
              {selectedIds.size > 0 && (
                <>
                  <div className="flex-1" />
                  <button
                    onClick={() => setShowBulkModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a3a6b] text-white text-xs font-medium rounded-lg hover:bg-[#0f2548] transition-colors"
                  >
                    <Tag className="w-3.5 h-3.5" /> Recategorize
                  </button>
                  <button
                    onClick={() => setRefineQuestions(questions.filter(q => selectedIds.has(q.$id)))}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 transition-colors"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Refine Explanations
                  </button>
                  <button
                    onClick={() => { setSelectedIds(new Set()); setSelectAllMatching(false); }}
                    className="text-xs text-[#5a6a7e] hover:text-[#1a2435] px-2"
                  >
                    Clear
                  </button>
                </>
              )}
            </div>

            {/* "Select all matching" banner — shown when the whole page is selected but not yet all matching */}
            {selectedIds.size === questions.length && !selectAllMatching && total !== null && total > questions.length && (
              <div className="flex items-center gap-2 bg-[#1a3a6b]/5 border border-[#1a3a6b]/20 rounded-xl px-4 py-2.5 text-sm text-[#1a3a6b]">
                <span>All {questions.length} on this page are selected.</span>
                <button
                  onClick={() => setSelectAllMatching(true)}
                  className="font-semibold underline underline-offset-2 hover:opacity-70"
                >
                  Select all {total.toLocaleString()} matching entries
                </button>
              </div>
            )}
          </div>
        )}

        {/* Questions */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#5a6a7e] gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-20 text-[#5a6a7e] text-sm">No questions found.</div>
        ) : (
          <div className="space-y-4">
            {questions.map((q, i) => (
              <QuestionCard
                key={q.$id}
                q={q}
                index={startNum + i}
                selected={selectedIds.has(q.$id)}
                onSelect={() => toggleSelect(q.$id)}
                onEdit={() => setEditingQ(q)}
                onDelete={() => handleDelete(q.$id)}
                onToggleActive={() => handleToggleActive(q)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && (hasPrev || hasNext) && (
          <div className="flex items-center justify-between pt-1">
            <button
              onClick={goPrev}
              disabled={!hasPrev}
              className="flex items-center gap-1.5 px-4 py-2 border border-[#dde3ed] rounded-xl text-sm text-[#5a6a7e] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <span className="text-sm text-[#5a6a7e]">
              {startNum}–{startNum + questions.length - 1}
              {total !== null && ` of ${total.toLocaleString()}`}
            </span>
            <button
              onClick={goNext}
              disabled={!hasNext}
              className="flex items-center gap-1.5 px-4 py-2 border border-[#dde3ed] rounded-xl text-sm text-[#5a6a7e] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editingQ && (
        <EditModal
          question={editingQ}
          onSave={handleSave}
          onClose={() => setEditingQ(null)}
        />
      )}

      {/* Refine explanations modal */}
      {refineQuestions && (
        <RefineModal
          questions={refineQuestions}
          onExplanationUpdated={(id, explanation) =>
            setQuestions(prev => prev.map(q => q.$id === id ? { ...q, explanation } : q))
          }
          onClose={() => {
            setRefineQuestions(null);
            setSelectedIds(new Set());
            setSelectAllMatching(false);
            fetchPage(cursors[pageIdx] ?? null, { search, subjectId, moduleId, topicId, subtopicId, difficulty });
          }}
        />
      )}

      {/* Bulk recategorize modal */}
      {showBulkModal && (
        <BulkRecategorizeModal
          count={selectAllMatching ? (total ?? selectedIds.size) : selectedIds.size}
          allMatching={selectAllMatching}
          onApply={handleBulkRecategorize}
          onClose={() => setShowBulkModal(false)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium z-50 ${
          toast.ok ? "bg-green-600 text-white" : "bg-red-600 text-white"
        }`}>
          {toast.ok
            ? <CheckCircle2 className="w-4 h-4" />
            : <AlertCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}
